import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import useZDUser from 'services/help-center/queries/useZDUser';
import useStatsigGate from 'modules/common/useStatsigGate';
import { FeatureGates } from 'modules/common/constants/featuregates';
import {
    requestedTicketsQueryFn,
    requestedTicketsQueryKey,
} from 'services/help-center/queries/useSearchTickets/requestedTickets';
import fetchTickets from 'services/help-center/queries/useSearchTickets/useSearchTickets.request';
import { OPEN_MESSAGES_STATUSES, CLOSED_MESSAGES_STATUSES } from 'services/help-center/constants';

export default function usePrefetchZDUserData() {
    const isZendeskMemberProjectionEnabled = useStatsigGate(FeatureGates.ZendeskMemberProjection);
    const queryClient = useQueryClient();
    const { data: zdUser } = useZDUser();

    useEffect(() => {
        if (zdUser?.id && isZendeskMemberProjectionEnabled) {
            // One call warms both tabs: they are derived from the same all-status list.
            queryClient.prefetchQuery({
                queryKey: requestedTicketsQueryKey(zdUser.id),
                queryFn: requestedTicketsQueryFn(queryClient, zdUser.id),
                staleTime: 1000 * 60 * 10, // 10 minutes
            });
            return undefined;
        }

        if (zdUser?.id) {
            // Prefetch open messages immediately (highest priority)
            queryClient.prefetchQuery({
                queryKey: ['messageCenterTickets', zdUser.id, '', OPEN_MESSAGES_STATUSES],
                queryFn: () => fetchTickets(zdUser.id, '', OPEN_MESSAGES_STATUSES),
                staleTime: 1000 * 60 * 10, // 10 minutes
            });

            // Prefetch closed messages after a short delay (lower priority)
            const timer = setTimeout(() => {
                queryClient.prefetchQuery({
                    queryKey: ['messageCenterTickets', zdUser.id, '', CLOSED_MESSAGES_STATUSES],
                    queryFn: () => fetchTickets(zdUser.id, '', CLOSED_MESSAGES_STATUSES),
                    staleTime: 1000 * 60 * 10, // 10 minutes
                });
            }, 2000); // 2 second delay to not overwhelm initial app load

            return () => clearTimeout(timer);
        }
        return undefined;
    }, [zdUser?.id, queryClient, isZendeskMemberProjectionEnabled]);

    function invalidateOpenMessagesCache() {
        if (zdUser?.id && isZendeskMemberProjectionEnabled) {
            queryClient.invalidateQueries({ queryKey: requestedTicketsQueryKey(zdUser.id), exact: true });
            return;
        }
        if (zdUser?.id) {
            const memberCenterTicketsQuery = queryClient.getQueryCache().find({
                queryKey: ['messageCenterTickets', zdUser.id, '', OPEN_MESSAGES_STATUSES],
            });
            if (memberCenterTicketsQuery) {
                queryClient.invalidateQueries({
                    queryKey: ['messageCenterTickets', zdUser.id, '', OPEN_MESSAGES_STATUSES],
                });
            }
        }
    }

    return {
        invalidateOpenMessagesCache,
    };
}
